import { useEffect, useMemo, useRef, useState } from 'react';
import { Play, Pause, RotateCcw, Minus, Plus, Circle, Volume2, Repeat, X, Youtube } from 'lucide-react';
import Waveform from './Waveform';
import useYouTubePlayer, { extractYouTubeId, checkYouTubeEmbeddable } from '@/lib/useYouTubePlayer';

const SPEED_OPTIONS = [0.5, 0.75, 1];

export default function PlayAlong({ song }) {
  const timeline = useMemo(() => song?.chord_timeline || [], [song?.chord_timeline]);
  const [bpm, setBpm] = useState(song?.bpm || 90);
  const [playing, setPlaying] = useState(false);
  const [idx, setIdx] = useState(0);
  const [subBeat, setSubBeat] = useState(0);
  const [metronomeOn, setMetronomeOn] = useState(true);
  const [tapping, setTapping] = useState([]);
  const [audioReady, setAudioReady] = useState(false);
  const [speed, setSpeed] = useState(1);
  // Loop state: null (off) | 'awaiting-start' | 'awaiting-end' | 'active'
  const [loopMode, setLoopMode] = useState(null);
  const [loopStartBeat, setLoopStartBeat] = useState(null);
  const [loopEndBeat, setLoopEndBeat] = useState(null);
  // Sync offset in seconds (video time - chord time). Positive = the actual song starts
  // AFTER t=0 in the video (music-video intros, artist logos, silence…).
  // Priority: user override (localStorage) > backend admin-curated value > 0.
  const syncKey = song?.id ? `maestro:sync-offset:${song.id}` : null;
  const [syncOffset, setSyncOffset] = useState(() => {
    try {
      if (!syncKey) return song?.sync_offset || 0;
      const raw = localStorage.getItem(syncKey);
      if (raw != null) {
        const v = parseFloat(raw);
        if (Number.isFinite(v)) return v;
      }
      return song?.sync_offset || 0;
    } catch { return song?.sync_offset || 0; }
  });
  const persistSyncOffset = (val) => {
    setSyncOffset(val);
    try { if (syncKey) localStorage.setItem(syncKey, String(val)); } catch { /* ignore */ }
  };
  const nudgeSync = (deltaSec) => {
    const next = Math.max(-30, Math.min(60, +(syncOffset + deltaSec).toFixed(2)));
    persistSyncOffset(next);
  };

  // Refs
  const rafRef = useRef(null);
  const audioCtxRef = useRef(null);
  const audioElRef = useRef(null);
  const ytHostRef = useRef(null);
  const beatsAccRef = useRef(0);      // accumulated beats since start (survives BPM changes & pauses)
  const lastTickRef = useRef(0);      // performance.now() at last RAF tick
  const lastWholeBeatRef = useRef(-1);// last integer beat we clicked on

  const hasAudio = !!song?.audio_url;
  // Prefer video_url_alt (admin-curated embeddable version) over the original video_url.
  const effectiveVideoUrl = song?.video_url_alt || song?.video_url;
  const usingAltVideo = !!song?.video_url_alt && song.video_url_alt !== song.video_url;
  // Fallback: if no direct audio file, try to use the YouTube tutorial video as master clock.
  const ytId = useMemo(() => (hasAudio ? null : extractYouTubeId(effectiveVideoUrl)), [hasAudio, effectiveVideoUrl]);
  // Preflight embeddability check via oEmbed. `null` = still checking, `true` = OK, `false` = blocked.
  const [ytEmbeddable, setYtEmbeddable] = useState(null);
  useEffect(() => {
    setYtEmbeddable(null);
    if (!ytId) return;
    let cancelled = false;
    checkYouTubeEmbeddable(ytId).then((ok) => { if (!cancelled) setYtEmbeddable(ok); });
    return () => { cancelled = true; };
  }, [ytId]);
  const wantYT = !hasAudio && !!ytId && ytEmbeddable !== false;
  const yt = useYouTubePlayer(wantYT ? ytId : null, ytHostRef);
  // If the embed fails (owner disabled embedding), transparently fall back to metronome mode.
  const hasYT = wantYT && !yt.error;
  const usingRealAudio = hasAudio || hasYT;
  const beatMs = 60000 / bpm;

  // Cumulative beat offsets for each pill (memoized so we don't recompute per RAF tick).
  const beatOffsets = useMemo(() => {
    const arr = new Array(timeline.length);
    let acc = 0;
    for (let i = 0; i < timeline.length; i++) {
      arr[i] = acc;
      acc += timeline[i].beats;
    }
    return arr;
  }, [timeline]);

  // Reset BPM & progress when song changes
  useEffect(() => {
    setBpm(song?.bpm || 90);
    beatsAccRef.current = 0;
    lastWholeBeatRef.current = -1;
    setIdx(0); setSubBeat(0); setPlaying(false);
    setAudioReady(false);
    setLoopMode(null);
    setLoopStartBeat(null);
    setLoopEndBeat(null);
    if (audioElRef.current) {
      try { audioElRef.current.pause(); audioElRef.current.currentTime = 0; } catch { /* ignore */ }
    }
  }, [song?.id, song?.bpm]);

  // Apply playback speed + preserve pitch on the audio element / YouTube player.
  useEffect(() => {
    const el = audioElRef.current;
    if (el) {
      try {
        el.playbackRate = speed;
        el.preservesPitch = true;
        el.mozPreservesPitch = true;
        el.webkitPreservesPitch = true;
      } catch { /* ignore */ }
    }
    if (hasYT && yt.ready) {
      yt.setPlaybackRate(speed);
    }
  }, [speed, audioReady, hasYT, yt]);

  // Cleanup on unmount
  useEffect(() => () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (audioCtxRef.current) audioCtxRef.current.close().catch(() => {});
    if (audioElRef.current) { try { audioElRef.current.pause(); } catch { /* ignore */ } }
  }, []);

  const ensureAudio = () => {
    if (!audioCtxRef.current) {
      try {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      } catch { audioCtxRef.current = null; }
    }
    if (audioCtxRef.current && audioCtxRef.current.state === 'suspended') {
      audioCtxRef.current.resume().catch(() => {});
    }
  };

  const click = (accent = false) => {
    const ctx = audioCtxRef.current;
    if (!ctx) return;
    try {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = accent ? 1400 : 1000;
      gain.gain.setValueAtTime(accent ? 0.4 : 0.25, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.05);
      osc.connect(gain).connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.05);
    } catch {}
  };

  // Main animation loop.
  // • When song.audio_url exists → we sync to `audio.currentTime` (drift-free).
  // • Otherwise → we accumulate elapsed time via performance.now() deltas.
  useEffect(() => {
    if (!playing) return;
    lastTickRef.current = performance.now();

    const tick = () => {
      const now = performance.now();
      if (hasAudio && audioElRef.current) {
        // Audio is the master clock: derive beats directly.
        // Sync offset: audio time > syncOffset before chords start.
        const t = Math.max(0, audioElRef.current.currentTime - syncOffset);
        beatsAccRef.current = t * (bpm / 60);
        lastTickRef.current = now;
      } else if (hasYT && yt.ready) {
        // YouTube video is the master clock (with intro offset).
        const t = Math.max(0, yt.getCurrentTime() - syncOffset);
        beatsAccRef.current = t * (bpm / 60);
        lastTickRef.current = now;
      } else {
        const delta = now - lastTickRef.current;
        lastTickRef.current = now;
        beatsAccRef.current += (delta / beatMs) * speed;
      }
      const totalBeats = beatsAccRef.current;

      // Section looping: if active and we've crossed the end, jump back to start.
      if (loopMode === 'active' && loopStartBeat != null && loopEndBeat != null && totalBeats >= loopEndBeat) {
        beatsAccRef.current = loopStartBeat;
        lastWholeBeatRef.current = Math.floor(loopStartBeat) - 1;
        const startSec = loopStartBeat * (60 / bpm) + syncOffset;
        if (hasAudio && audioElRef.current) {
          try { audioElRef.current.currentTime = startSec; } catch { /* ignore */ }
        } else if (hasYT && yt.ready) {
          yt.seek(startSec);
        }
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      // Locate current chord within timeline
      let acc = 0;
      let curIdx = -1;
      for (let i = 0; i < timeline.length; i++) {
        if (acc + timeline[i].beats > totalBeats) {
          curIdx = i;
          break;
        }
        acc += timeline[i].beats;
      }
      // End of timeline reached.
      // - Metronome-only: infinite loop (rewind to 0)
      // - Real audio (file or YouTube): auto-pause so the song plays out naturally.
      if (curIdx === -1) {
        if (usingRealAudio) {
          setPlaying(false);
          if (hasAudio && audioElRef.current) {
            try { audioElRef.current.pause(); } catch { /* ignore */ }
          } else if (hasYT && yt.ready) {
            yt.pause();
          }
          setIdx(timeline.length - 1);
          setSubBeat(Math.max(0, timeline[timeline.length - 1].beats - 1));
          return;
        }
        beatsAccRef.current = 0;
        lastWholeBeatRef.current = -1;
        setIdx(0); setSubBeat(0);
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      // Metronome click on each whole beat (accent on downbeat of current chord).
      const wholeBeat = Math.floor(totalBeats);
      if (metronomeOn && wholeBeat !== lastWholeBeatRef.current) {
        lastWholeBeatRef.current = wholeBeat;
        const accentBeat = wholeBeat === Math.floor(acc); // first beat of current chord
        click(accentBeat);
      }

      setIdx(curIdx);
      setSubBeat(Math.floor(totalBeats - acc));
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [playing, timeline, beatMs, metronomeOn, hasAudio, hasYT, yt, bpm, speed, loopMode, loopStartBeat, loopEndBeat, usingRealAudio, syncOffset]);

  // Play: resume from current position, or start fresh if we're at 0 or ended
  const play = () => {
    ensureAudio();
    lastWholeBeatRef.current = Math.floor(beatsAccRef.current) - 1;
    if (hasAudio && audioElRef.current) {
      // Align the audio with the current beats offset (for seek/resume).
      const targetTime = beatsAccRef.current * (60 / bpm) + syncOffset;
      try {
        if (Math.abs(audioElRef.current.currentTime - targetTime) > 0.15) {
          audioElRef.current.currentTime = targetTime;
        }
        audioElRef.current.play().catch((err) => console.debug('audio play blocked', err));
      } catch (err) { console.debug('audio play error', err); }
    } else if (hasYT && yt.ready) {
      const targetTime = beatsAccRef.current * (60 / bpm) + syncOffset;
      const cur = yt.getCurrentTime();
      if (Math.abs(cur - targetTime) > 0.25) yt.seek(targetTime);
      yt.play();
      // Watchdog: if YouTube fails to actually start playing within 3.5s (Erreur 153,
      // undocumented restrictions, etc.), silently fall back to metronome mode.
      const startTime = yt.getCurrentTime();
      setTimeout(() => {
        try {
          const advanced = yt.getCurrentTime() - startTime;
          if (advanced < 0.15) yt.signalError();
        } catch { /* ignore */ }
      }, 3500);
    }
    setPlaying(true);
  };
  const pause = () => {
    setPlaying(false);
    if (hasAudio && audioElRef.current) {
      try { audioElRef.current.pause(); } catch { /* ignore */ }
    } else if (hasYT && yt.ready) {
      yt.pause();
    }
  };
  const reset = () => {
    setPlaying(false);
    beatsAccRef.current = 0;
    lastWholeBeatRef.current = -1;
    setIdx(0); setSubBeat(0);
    if (hasAudio && audioElRef.current) {
      try { audioElRef.current.pause(); audioElRef.current.currentTime = syncOffset; } catch { /* ignore */ }
    } else if (hasYT && yt.ready) {
      yt.pause();
      yt.seek(syncOffset);
    }
  };

  const tap = () => {
    ensureAudio();
    const now = performance.now();
    const arr = [...tapping.filter(t => now - t < 3000), now];
    setTapping(arr);
    if (arr.length >= 3) {
      const intervals = [];
      for (let i = 1; i < arr.length; i++) intervals.push(arr[i] - arr[i - 1]);
      const avg = intervals.reduce((a, b) => a + b, 0) / intervals.length;
      const newBpm = Math.max(40, Math.min(220, Math.round(60000 / avg)));
      setBpm(newBpm);
    }
  };

  // ---- Loop helpers -----------------------------------------------------
  const startLoopFlow = () => {
    if (loopMode === 'active' || loopMode === 'awaiting-end') {
      // Any state other than idle → clear.
      setLoopMode(null);
      setLoopStartBeat(null);
      setLoopEndBeat(null);
      return;
    }
    if (loopMode === 'awaiting-start') {
      setLoopMode(null);
      return;
    }
    setLoopStartBeat(null);
    setLoopEndBeat(null);
    setLoopMode('awaiting-start');
  };

  const handlePillClick = (i) => {
    // Loop assignment flow takes priority over seek.
    if (loopMode === 'awaiting-start') {
      setLoopStartBeat(beatOffsets[i]);
      setLoopMode('awaiting-end');
      return;
    }
    if (loopMode === 'awaiting-end') {
      const endBeat = beatOffsets[i] + timeline[i].beats;
      const startBeat = loopStartBeat ?? 0;
      if (endBeat <= startBeat) {
        // User picked an earlier pill → swap.
        setLoopStartBeat(endBeat - timeline[i].beats);
        setLoopEndBeat(startBeat + 0.001);
      } else {
        setLoopEndBeat(endBeat);
      }
      setLoopMode('active');
      return;
    }
    // Normal seek
    const acc = beatOffsets[i];
    beatsAccRef.current = acc;
    lastWholeBeatRef.current = acc - 1;
    const targetSec = acc * (60 / bpm) + syncOffset;
    if (hasAudio && audioElRef.current) {
      try { audioElRef.current.currentTime = targetSec; } catch { /* ignore */ }
    } else if (hasYT && yt.ready) {
      yt.seek(targetSec);
    }
    setIdx(i);
    setSubBeat(0);
  };

  // Beat offsets → seconds (for Waveform loop region overlay)
  const loopStartTime = loopStartBeat != null ? loopStartBeat * (60 / bpm) : null;
  const loopEndTime = loopEndBeat != null ? loopEndBeat * (60 / bpm) : null;

  const loopLabel =
    loopMode === 'awaiting-start' ? 'Choisir début…' :
    loopMode === 'awaiting-end'   ? 'Choisir fin…' :
    loopMode === 'active'         ? 'Boucle active' :
    'Boucle';

  if (!timeline.length) {
    return (
      <div className="card-stone p-6">
        <div className="overline text-orange-400 mb-2">Métronome Studio</div>
        <div className="text-stone-400 text-sm mb-4">
          Ce morceau n'a pas encore de timeline d'accords, mais tu peux quand même utiliser le métronome pour caler ton tempo.
        </div>
        <MetronomeControls bpm={bpm} setBpm={setBpm} tap={tap} tapping={tapping} metronomeOn={metronomeOn} setMetronomeOn={setMetronomeOn} />
      </div>
    );
  }

  const current = timeline[idx];

  return (
    <div className="card-stone p-6" data-testid="play-along">
      {!hasAudio && ytId && (ytEmbeddable === false || yt.error) && (
        <div
          data-testid="playalong-blocked-banner"
          className="mb-5 rounded-xl border border-amber-800/60 bg-amber-950/30 p-4 flex items-start gap-3"
        >
          <div className="shrink-0 w-9 h-9 rounded-lg bg-amber-900/50 border border-amber-700/60 flex items-center justify-center text-amber-300">
            <Youtube size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-amber-100 leading-snug">
              Cette vidéo n'est pas intégrable dans Maestro
            </div>
            <div className="text-xs text-amber-200/80 mt-1 leading-relaxed">
              L'artiste (ou son label) a désactivé la lecture intégrée sur les sites tiers. Ton Play-Along fonctionne quand même en <span className="text-amber-100 font-medium">mode métronome</span> — accords, tempo, vitesse, boucle, tout reste actif.
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              <a
                href={song.video_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-red-900/40 hover:bg-red-900/60 border border-red-800/60 text-red-200 transition-colors"
                data-testid="playalong-open-yt"
              >
                <Youtube size={11} /> Écouter le morceau sur YouTube
              </a>
              <a
                href={`https://www.youtube.com/results?search_query=${encodeURIComponent(`${song.title} ${song.artist} karaoke instrumental`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-emerald-900/40 hover:bg-emerald-900/60 border border-emerald-800/60 text-emerald-200 transition-colors"
                data-testid="playalong-karaoke-booster"
                title="Cherche une version karaoké/instrumental qui, elle, est souvent intégrable"
              >
                🎤 Chercher version karaoké
              </a>
            </div>
          </div>
        </div>
      )}
      {hasAudio && (
        <audio
          ref={audioElRef}
          src={song.audio_url}
          preload="metadata"
          onLoadedMetadata={() => setAudioReady(true)}
          onCanPlay={() => setAudioReady(true)}
          data-testid="playalong-audio"
          className="hidden"
        />
      )}
      {wantYT && !yt.error && (
        <div className="mb-4 rounded-xl overflow-hidden border border-stone-800 bg-black aspect-video max-w-md mx-auto">
          <div ref={ytHostRef} className="w-full h-full" data-testid="playalong-yt-host" />
        </div>
      )}
      {wantYT && !yt.error && (
        <div className="flex items-center justify-center gap-1.5 text-[11px] text-orange-400/70 -mt-2 mb-4">
          <Youtube size={11} className="opacity-80" />
          <span>Lecture streamée depuis YouTube · Maestro ne redistribue pas les enregistrements</span>
        </div>
      )}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="overline text-orange-400 flex items-center gap-2">
            Play-Along · Métronome Studio
            {hasAudio && (
              <span
                data-testid="playalong-audio-badge"
                title="L'audio original est synchronisé avec les accords"
                className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-950/60 text-emerald-300 border border-emerald-800/60 normal-case tracking-normal"
              >
                <Volume2 size={10} /> Audio synchro
              </span>
            )}
            {hasYT && (
              <span
                data-testid="playalong-yt-badge"
                title={usingAltVideo ? "Version alternative (karaoké/live) synchronisée par Maestro" : "La vidéo YouTube sert de piste audio synchronisée"}
                className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-red-950/60 text-red-300 border border-red-800/60 normal-case tracking-normal"
              >
                <Youtube size={10} /> {usingAltVideo ? 'YouTube alt' : 'YouTube synchro'}
              </span>
            )}
          </div>
          <div className="text-stone-400 text-sm">
            {hasAudio
              ? 'Les accords défilent avec la piste audio · seek possible'
              : hasYT
                ? (yt.ready ? 'Les accords défilent avec la vidéo YouTube · seek possible' : 'Chargement du lecteur YouTube…')
                : 'Les accords défilent au tempo · boucle infinie'}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Sync offset control (only relevant when we have real audio) */}
          {usingRealAudio && (
            <div
              className="inline-flex items-center gap-1 rounded-full bg-stone-900 border border-stone-800 pl-2 pr-0.5 py-0.5"
              data-testid="sync-offset-group"
              title="Décale la vidéo par rapport aux accords quand l'intro du clip commence avant la musique"
            >
              <span className="text-[10px] text-stone-500 uppercase tracking-wider">Sync</span>
              <button
                onClick={() => nudgeSync(-0.5)}
                className="w-6 h-6 rounded-full text-stone-400 hover:text-stone-100 hover:bg-stone-800 flex items-center justify-center"
                data-testid="sync-minus"
                title="Reculer la vidéo de 0,5 s"
              >
                <Minus size={12} />
              </button>
              <span
                className={`text-xs tabular-nums w-11 text-center ${syncOffset !== 0 ? 'text-orange-300' : 'text-stone-400'}`}
                data-testid="sync-offset-value"
                onDoubleClick={() => persistSyncOffset(0)}
              >
                {syncOffset > 0 ? '+' : ''}{syncOffset.toFixed(1)}s
              </span>
              <button
                onClick={() => nudgeSync(0.5)}
                className="w-6 h-6 rounded-full text-stone-400 hover:text-stone-100 hover:bg-stone-800 flex items-center justify-center"
                data-testid="sync-plus"
                title="Avancer la vidéo de 0,5 s"
              >
                <Plus size={12} />
              </button>
            </div>
          )}

          {/* Speed selector */}
          <div className="inline-flex rounded-full bg-stone-900 border border-stone-800 p-0.5" data-testid="speed-group">
            {SPEED_OPTIONS.map((s) => (
              <button
                key={s}
                data-testid={`speed-${String(s).replace('.', '_')}x`}
                onClick={() => setSpeed(s)}
                className={`px-2.5 py-1 text-xs rounded-full transition-colors ${
                  speed === s ? 'bg-orange-700 text-white' : 'text-stone-400 hover:text-stone-200'
                }`}
                title={`Vitesse ${s}× (hauteur préservée)`}
              >
                {s}×
              </button>
            ))}
          </div>

          {/* Loop control */}
          <button
            data-testid="loop-toggle"
            onClick={startLoopFlow}
            className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-full border transition-colors ${
              loopMode === 'active'
                ? 'bg-orange-950/60 border-orange-700 text-orange-300'
                : loopMode
                  ? 'bg-amber-950/40 border-amber-700 text-amber-300 animate-pulse'
                  : 'border-stone-700 text-stone-400 hover:border-stone-500'
            }`}
            title="Boucle de section : sélectionne une pastille de début puis une pastille de fin"
          >
            {loopMode === 'active' ? <X size={13} /> : <Repeat size={13} />}
            {loopLabel}
          </button>

          {playing ? (
            <button data-testid="playalong-pause" onClick={pause} className="btn-ghost inline-flex items-center gap-2">
              <Pause size={16} /> Pause
            </button>
          ) : (
            <button data-testid="playalong-play" onClick={play} className="btn-primary inline-flex items-center gap-2">
              <Play size={16} /> {beatsAccRef.current > 0 ? 'Reprendre' : 'Jouer'}
            </button>
          )}
          <button data-testid="playalong-reset" onClick={reset} className="btn-ghost inline-flex items-center gap-2" title="Retour au début">
            <RotateCcw size={16} />
          </button>
        </div>
      </div>

      {hasAudio && audioReady && (
        <Waveform
          audioUrl={song.audio_url}
          audioRef={audioElRef}
          loopStartTime={loopStartTime}
          loopEndTime={loopEndTime}
          seekDisabled={loopMode === 'awaiting-start' || loopMode === 'awaiting-end'}
        />
      )}

      <MetronomeControls bpm={bpm} setBpm={setBpm} tap={tap} tapping={tapping} metronomeOn={metronomeOn} setMetronomeOn={setMetronomeOn} />

      <div className="relative py-8">
        <div className="text-center">
          <div className="overline text-stone-500">Accord actuel</div>
          <div
            data-testid="playalong-current-chord"
            className={`font-display text-7xl md:text-8xl mt-1 transition-colors ${
              playing ? 'text-orange-500' : 'text-stone-100'
            }`}
          >
            {current?.chord}
          </div>
          {current && (
            <div className="mt-3 flex justify-center gap-2">
              {Array.from({ length: current.beats }).map((_, i) => (
                <Circle
                  key={i}
                  size={12}
                  className={playing && i === subBeat ? 'text-orange-400 fill-orange-400' : 'text-stone-600'}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-2 no-scroll-bar">
        {timeline.map((c, i) => {
          const prevSection = i > 0 ? timeline[i - 1].section : '__start__';
          const showSection = c.section && c.section !== prevSection;
          const startBeat = beatOffsets[i];
          const endBeat = startBeat + c.beats;
          const inLoop =
            loopMode === 'active' &&
            loopStartBeat != null && loopEndBeat != null &&
            startBeat >= loopStartBeat - 0.001 && endBeat <= loopEndBeat + 0.001;
          const isLoopStart = loopMode !== null && loopStartBeat != null && Math.abs(startBeat - loopStartBeat) < 0.001;
          const isLoopEnd = loopMode !== null && loopEndBeat != null && Math.abs(endBeat - loopEndBeat) < 0.001;
          return (
            <div key={i} className="flex flex-col gap-1 shrink-0 items-start">
              {showSection && (
                <span className="text-[9px] uppercase tracking-widest text-orange-400 font-bold px-2 py-0.5 rounded-full bg-orange-950/40 border border-orange-800/50 whitespace-nowrap">
                  {c.section}
                </span>
              )}
              <button
                data-testid={`playalong-pill-${i}`}
                onClick={() => handlePillClick(i)}
                className={`shrink-0 px-4 py-2 rounded-full text-sm border transition-colors cursor-pointer ${
                  i === idx
                    ? 'bg-orange-700 border-orange-600 text-white shadow-lg shadow-orange-900/40'
                    : inLoop
                      ? 'bg-orange-950/40 border-orange-700/70 text-orange-200'
                      : i < idx
                        ? 'bg-stone-800 border-stone-700 text-stone-400'
                        : 'bg-transparent border-stone-700 text-stone-300 hover:border-stone-500'
                } ${(isLoopStart || isLoopEnd) ? 'ring-2 ring-amber-400 ring-offset-1 ring-offset-stone-950' : ''} ${
                  loopMode === 'awaiting-start' || loopMode === 'awaiting-end' ? 'hover:bg-amber-950/30 hover:border-amber-600' : ''
                }`}
                title={c.target_note ? `Note-cible chant : ${c.target_note}` : undefined}
              >
                {c.chord} <span className="opacity-60 text-xs">· {c.beats}</span>
                {c.target_note && (
                  <span className="ml-1.5 text-[10px] px-1 rounded bg-emerald-950/40 text-emerald-300 border border-emerald-800/50">
                    ♪{c.target_note}
                  </span>
                )}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MetronomeControls({ bpm, setBpm, tap, tapping, metronomeOn, setMetronomeOn }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-center mt-3 mb-2">
      <div className="flex items-center gap-2">
        <button data-testid="bpm-down" onClick={() => setBpm(b => Math.max(40, b - 5))} className="p-2 rounded-full bg-stone-800 hover:bg-stone-700"><Minus size={14} /></button>
        <div className="flex-1 text-center">
          <div className="overline text-stone-500">Tempo</div>
          <div data-testid="bpm-value" className="font-display text-3xl">{bpm} <span className="text-sm text-stone-400">BPM</span></div>
        </div>
        <button data-testid="bpm-up" onClick={() => setBpm(b => Math.min(220, b + 5))} className="p-2 rounded-full bg-stone-800 hover:bg-stone-700"><Plus size={14} /></button>
      </div>
      <button data-testid="tap-tempo" onClick={tap} className="btn-ghost inline-flex items-center justify-center gap-2">
        <Circle size={14} className={tapping.length ? 'text-orange-400' : ''} />
        Tap Tempo ({tapping.length})
      </button>
      <label className="inline-flex items-center gap-2 justify-center text-sm text-stone-300 cursor-pointer select-none" data-testid="metronome-toggle-wrap">
        <input
          type="checkbox"
          checked={metronomeOn}
          onChange={(e) => setMetronomeOn(e.target.checked)}
          data-testid="metronome-toggle"
          className="accent-orange-600 w-4 h-4"
        />
        Clic métronome
      </label>
    </div>
  );
}
